import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Snackbar,
  CircularProgress,
  Autocomplete
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { da } from "date-fns/locale";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { FormControlLabel, Switch } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useApi } from "../api/useApi";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToParentElement } from '@dnd-kit/modifiers';

const fieldLabels = {
  make: "Make",
  model: "Model",
  vin: "VIN",
  carType: "Car Type",
  fuelType: "Fuel Type",
  firstRegistrationDate: "First Registration Date",
  km: "Km"
};

const CreateListingPage = () => {
  const navigate = useNavigate();
  const { authFetch } = useApi();
  const [showTechFields, setShowTechFields] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearchedVin, setHasSearchedVin] = useState(false);
  const [images, setImages] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [selectedMakeId, setSelectedMakeId] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [selectOptions, setSelectOptions] = useState({ makes: [], models: [], carTypes: [], fuelTypes: [] });
  const [formValues, setFormValues] = useState({
    make: "",
    model: "",
    vin: "",
    variant: "",
    registrationNumber: "",
    color: "",
    price: "",
    seats: "",
    doors: "",
    onHold: false,
    externalVisible: true,
    hp: "",
    km: "",
    description: "",
    internalNotes: "",
    firstRegistrationDate: new Date("2020-01-01"),
    carType: "",
    fuelType: "",
    vatReclaimable: false
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = images.findIndex(i => i.id === active.id);
    const newIndex = images.findIndex(i => i.id === over.id);
    setImages((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  useEffect(() => {
    const fetchInitialOptions = async () => {
      try {
        const [makesRes, fuelTypesRes, carTypesRes] = await Promise.all([
          authFetch(`/admin/listing/make`),
          authFetch(`/admin/listing/fueltype`),
          authFetch(`/admin/listing/cartype`),
        ]);

        const [makes, fuelTypes, carTypes] = await Promise.all([makesRes.json(), fuelTypesRes.json(), carTypesRes.json()]);
        setSelectOptions((prev) => ({ ...prev, makes, fuelTypes, carTypes }));
      } catch (err) {
        console.error("Failed to fetch select options", err);
      }
    };
    fetchInitialOptions();
  }, [authFetch]);

  useEffect(() => {
    const fetchModels = async () => {
      if (selectedMakeId !== null) {
        try {
          const res = await authFetch(`/admin/listing/model/${selectedMakeId}`);
          const models = await res.json();
          setSelectOptions((prev) => ({ ...prev, models }));
        } catch (err) {
          console.error("Failed to fetch models", err);
        }
      }
    };
    fetchModels();
  }, [selectedMakeId, authFetch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    if (name === "make" && !hasSearchedVin) setSelectedMakeId(value);
    if (name === "model" && !hasSearchedVin) setSelectedModelId(value);

    setFormValues((prev) => ({ ...prev, [name]: fieldValue }));
  };

  const handleChangeDate = (date) => {
    setFormValues((prev) => ({ ...prev, firstRegistrationDate: date }));
  };

  const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const validateForm = (form, images) => {
    const requiredFields = Object.keys(fieldLabels);

    const missingFields = requiredFields.filter((field) => {
      const value = form[field];
      return value === null || value === undefined || value === "";
    });

    return missingFields.length === 0
      ? { valid: true }
      : {
        valid: false,
        missingFields,
      };
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const filesWithPreview = acceptedFiles.map((file) => ({
        id: crypto.randomUUID(),     // 👈 unique id for DnD
        file,
        preview: URL.createObjectURL(file),
      }));
      setImages((prev) => [...prev, ...filesWithPreview]);
    },
    accept: { "image/*": [] },
  });

  const searchVin = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/admin/listing/search/${formValues.vin}`);

      if (!res.ok) throw new Error(await res.text());

      const car = await res.json();
      setHasSearchedVin(true);
      setSelectedMakeId(car.makeId);
      setSelectedModelId(car.modelId);

      setFormValues((prev) => ({
        ...prev,
        vin: car.vin,
        make: car.make,
        model: car.model,
        variant: car.variant,
        firstRegistrationDate: new Date(car.firstRegistrationDate),
        fuelType: car.fuelType,
        hp: car.hp,
      }));
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to search VIN. Please try again.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const result = validateForm(formValues, images); // or newCar

    if (!result.valid) {
      const missingLabels = result.missingFields?.map((f) => fieldLabels[f] || f) || [];

      setSnackbar({ open: true, message: `Missing required fields: ${missingLabels.join(", ")}`, severity: "error" });
      return;
    }

    const formData = new FormData();
    const formDataValues = {
      ...formValues,
      ...(selectedMakeId !== null && { makeId: selectedMakeId }),
      ...(selectedModelId !== null && { modelId: selectedModelId }),
      firstRegistrationDate: formValues.firstRegistrationDate
        ? formValues.firstRegistrationDate.toISOString().split("T")[0]
        : "",
    };

    // Force conversion of numeric fields
    ["doors", "seats", "hp", "km"].forEach((key) => {
      const val = formValues[key];
      if (val === "" || val === null || isNaN(Number(val))) {
        formDataValues[key] = null;
      } else {
        formDataValues[key] = parseInt(val, 10);
      }
    });

    Object.entries(formDataValues).forEach(([key, value]) => {
      formData.append(key, value == null ? "" : value);
    });

    images.forEach(({ file }) => {
      formData.append("images", file);
    })

    setLoading(true);
    try {
      const res = await authFetch(`/admin/listing`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error(await res.text());

      setSnackbar({ open: true, message: "Listing created successfully", severity: "success" });
      setTimeout(() => navigate("/admin/listings"), 1000);
    } catch (err) {
      console.error("Submit error:", err);
      setSnackbar({ open: true, message: "Failed to create listing. Please try again.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  function SortableThumb({ item, index, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: 'grab',
      opacity: isDragging ? 0.8 : 1,
    };

    return (
      <Grid item xs={4} sm={3} md={2} ref={setNodeRef} style={style}>
        <Box sx={{ position: "relative" }} {...attributes} {...listeners}>
          <Box
            component="img"
            src={item.preview}
            alt={`preview-${index}`}
            sx={{
              width: "100%",
              height: 80,
              objectFit: "cover",
              borderRadius: 1,
              boxShadow: 1,
              userSelect: 'none',
            }}
            draggable={false}
          />
          <IconButton
            size="small"
            onClick={() => {
              URL.revokeObjectURL(item.preview);
              onRemove();
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              backgroundColor: "rgba(255,255,255,0.8)",
              "&:hover": { backgroundColor: "rgba(255,255,255,1)" }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Grid>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Box maxWidth="md" mx="auto" mt={4}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Create New Listing
          </Typography>
          {/* Image Dropzone */}
          <Box>
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "grey.400",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: isDragActive ? "grey.100" : "transparent",
              }}
            >
              <input {...getInputProps()} />
              <UploadFileIcon sx={{ fontSize: 40, color: "grey.600", mb: 1 }} />
              <Typography variant="body1">
                {isDragActive
                  ? "Drop the images here..."
                  : "Drag and drop images here, or click to select files"}
              </Typography>
            </Box>

            {images.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToParentElement]}
              >
                <SortableContext items={images.map(i => i.id)} strategy={rectSortingStrategy}>
                  <Grid container spacing={2} mt={2}>
                    {images.map((item, index) => (
                      <SortableThumb
                        key={item.id}
                        item={item}
                        index={index}
                        onRemove={() => setImages((prev) => prev.filter((_, i) => i !== index))}
                      />
                    ))}
                  </Grid>
                </SortableContext>
              </DndContext>
            )}
          </Box>

          {/* VIN Field */}
          {/* VIN Field with Search Button */}
          <Box mt={4} display="flex" gap={2}>
            <TextField
              label="VIN"
              name="vin"
              fullWidth
              value={formValues.vin || ""}
              onChange={handleChange}
            />
            <Button
              variant="outlined"
              color="primary"
              sx={{ minWidth: 120 }}
              startIcon={!loading ? <SearchIcon /> : null}
              onClick={searchVin}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : "Search"}
            </Button>
          </Box>

          {/* Main Fields */}
          <Grid container spacing={2} mt={2} mb={2}>
            <Grid item xs={12} sm={6}>
              {hasSearchedVin ? (
                <TextField
                  name="make"
                  label="Make"
                  fullWidth
                  value={formValues.make || ""}
                  onChange={handleChange}
                />
              ) : (
                <Autocomplete
                  options={selectOptions.makes}
                  getOptionLabel={(option) => option.name}
                  value={selectOptions.makes.find(opt => opt.id === formValues.make) || null}
                  onChange={(_, newValue) => {
                    handleChange({ target: { name: 'make', value: newValue?.id || '' } });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Make" name="make" fullWidth />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {hasSearchedVin ? (
                <TextField
                  name="model"
                  label="Model"
                  fullWidth
                  value={formValues.model || ""}
                  onChange={handleChange}
                />
              ) : (
                <Autocomplete
                  options={selectOptions.models}
                  getOptionLabel={(option) => option.name}
                  value={selectOptions.models.find(opt => opt.id === formValues.model) || null}
                  onChange={(_, newValue) => {
                    handleChange({ target: { name: 'model', value: newValue?.id || '' } });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Model" name="model" fullWidth />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Variant"
                fullWidth
                name="variant"
                value={formValues.variant || ""}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Registration Number"
                fullWidth
                name="registrationNumber"
                value={formValues.registrationNumber || ""}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Km"
                fullWidth
                name="km"
                type="number"
                value={formValues.km || ""}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="First Registration Date"
                name="firstRegistrationDate"
                value={formValues.firstRegistrationDate || ""}
                onChange={handleChangeDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Car Type"
                fullWidth
                name="carType"
                value={formValues.carType || ""}
                onChange={handleChange}
              >
                {selectOptions.carTypes.map((option) => (
                  <MenuItem key={option.key} value={option.key || ""}>{option.display}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Fuel Type"
                fullWidth
                name="fuelType"
                value={formValues.fuelType || ""}
                onChange={handleChange}
              >
                {selectOptions.fuelTypes.map((option) => (
                  <MenuItem key={option.key} value={option.key || ""}>{option.display}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Color"
                fullWidth
                name="color"
                value={formValues.color || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price"
                fullWidth
                name="price"
                value={formValues.price || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.vatReclaimable}
                    onChange={handleChange}
                    name="vatReclaimable"
                    color="primary"
                  />
                }
                label={`VAT Reclaimable - (${formValues.vatReclaimable ? 'Yes' : 'No'})`}
              />
            </Grid>
          </Grid>

          {/* Description */}
          <Box mt={2}>
            <TextField
              label="Description"
              name="description"
              fullWidth
              multiline
              minRows={4}
              value={formValues.description || ""}
              onChange={handleChange}
            />
          </Box>
          <Box display="flex" gap={4} mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.onHold}
                  onChange={handleChange}
                  name="onHold"
                  color="primary"
                />
              }
              label="On Hold"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.externalVisible}
                  onChange={handleChange}
                  name="externalVisible"
                  color="primary"
                />
              }
              label="Visible to Buyers"
            />
          </Box>

          {/* Expandable: Technical Details */}
          <Box mt={2}>
            <Button
              variant="text"
              onClick={() => setShowTechFields((prev) => !prev)}
              startIcon={showTechFields ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showTechFields ? "Hide technical details" : "Show technical details"}
            </Button>

            <Collapse in={showTechFields}>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Seats"
                    fullWidth
                    name="seats"
                    type="number"
                    value={formValues.seats ?? ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Doors"
                    fullWidth
                    name="doors"
                    type="number"
                    value={formValues.doors ?? ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="HP"
                    fullWidth
                    name="hp"
                    type="number"
                    value={formValues.hp ?? ""}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>

          {/* Expandable: Internal Notes */}
          <Box mt={2}>
            <Button
              variant="text"
              onClick={() => setShowInternalNotes((prev) => !prev)}
              startIcon={showInternalNotes ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showInternalNotes ? "Hide internal notes" : "Show internal notes"}
            </Button>

            <Collapse in={showInternalNotes}>
              <Box mt={2}>
                <TextField
                  label="Internal Notes"
                  name="internalNotes"
                  fullWidth
                  multiline
                  minRows={4}
                  value={formValues.internalNotes || ""}
                  onChange={handleChange}
                />
              </Box>
            </Collapse>
          </Box>

          {/* Save Button */}
          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Save Listing
            </Button>
          </Box>
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default CreateListingPage;